<template>
  <MainDialog :show="show" :on-close="close" :persistent="busy" width="760" :dark="false">
    <v-card class="mg">
      <div class="mg__head">
        <div class="mg__title">
          <v-icon small class="mr-2">mdi-image-auto-adjust</v-icon>
          Mockup generieren
          <span v-if="mockup" class="mg__sub">{{ mockup.name }} · {{ mockup.aspectRatio || "–" }} · {{ mockup.orientation }}</span>
        </div>
        <v-btn icon small :disabled="busy" @click="close"><v-icon small>mdi-close</v-icon></v-btn>
      </div>

      <div class="mg__body">
        <!-- Links: Mockup-Vorschau bzw. Ergebnis -->
        <div class="mg__preview">
          <v-img v-if="resultUrl" :src="resultUrl" contain max-height="420" class="mg__img">
            <template v-slot:placeholder><div class="mg__ph"><v-progress-circular indeterminate size="28" color="primary" /><span>Ergebnis lädt …</span></div></template>
          </v-img>
          <v-img v-else-if="mockup" :src="mockup.thumbnail" contain max-height="420" class="mg__img mg__img--dim">
            <template v-slot:placeholder><div class="mg__ph"><v-progress-circular indeterminate size="28" color="grey" /><span>Mockup-Vorschau lädt …</span></div></template>
          </v-img>
          <div v-if="busy" class="mg__busy">
            <v-progress-circular indeterminate color="primary" size="36" />
            <span>{{ busyText }}</span>
          </div>
        </div>

        <!-- Rechts: Upload + Einstellungen -->
        <div class="mg__side">
          <div
            class="mg__drop"
            :class="{ 'is-over': dragOver, 'has-file': !!file }"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
            @click="!busy && $refs.fileInput.click()"
          >
            <input ref="fileInput" type="file" accept="image/*" hidden @change="onPick" />
            <template v-if="file">
              <img :src="filePreview" alt="" class="mg__drop-thumb" />
              <div class="mg__drop-meta">
                <b>{{ file.name }}</b>
                <small>{{ fileDims }} · {{ (file.size / 1024 / 1024).toFixed(1) }} MB</small>
                <small v-if="orientationHint" class="mg__warn">{{ orientationHint }}</small>
              </div>
            </template>
            <template v-else>
              <v-icon large color="grey lighten-1">mdi-tray-arrow-down</v-icon>
              <div class="mg__drop-text">Bild hierher ziehen<br /><small>oder klicken zum Auswählen</small></div>
            </template>
          </div>

          <v-select
            v-model="width"
            :items="widths"
            label="Breite des Ergebnisses"
            dense outlined hide-details
            class="mt-4"
            :disabled="busy"
          />

          <v-btn
            color="primary" depressed block class="mt-4"
            :disabled="!file || busy"
            :loading="busy"
            @click="generate"
          >
            <v-icon small left>mdi-creation</v-icon>Generieren
          </v-btn>

          <div v-if="resultUrl" class="mg__actions">
            <v-btn text small :href="resultUrl" target="_blank" rel="noopener"><v-icon small left>mdi-open-in-new</v-icon>Öffnen</v-btn>
            <v-btn text small @click="download"><v-icon small left>mdi-download</v-icon>Herunterladen</v-btn>
            <v-btn text small @click="copyUrl"><v-icon small left>mdi-link-variant</v-icon>{{ copied ? "Kopiert" : "URL kopieren" }}</v-btn>
          </div>

          <div v-if="error" class="mg__error">{{ error }}</div>
        </div>
      </div>
    </v-card>
  </MainDialog>
</template>

<script>
import MainDialog from "@/components/MainDialog";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { functions } from "../../../../fb";
import { copyToClipboard } from "@/utils";

export default {
  name: "MockupGeneratorDialog",
  components: { MainDialog },
  props: {
    show: { type: Boolean, default: false },
    mockup: { type: Object, default: null },
    onClose: { type: Function, required: true },
  },
  data() {
    return {
      file: null,
      filePreview: null,
      fileDims: "",
      fileOrientation: null,
      dragOver: false,
      width: 2000,
      widths: [
        { text: "1000 px (schnell)", value: 1000 },
        { text: "2000 px (wie Vorschau-Mail)", value: 2000 },
        { text: "3000 px", value: 3000 },
      ],
      busy: false,
      busyText: "",
      resultUrl: null,
      error: "",
      copied: false,
    };
  },
  computed: {
    orientationHint() {
      if (!this.fileOrientation || !this.mockup?.orientation) return "";
      if (this.fileOrientation === this.mockup.orientation) return "";
      return `Achtung: Bild ist ${this.fileOrientation === "landscape" ? "Querformat" : "Hochformat"}, das Mockup ist ${this.mockup.orientation}.`;
    },
  },
  watch: {
    show(v) { if (v) this.reset(); },
  },
  methods: {
    reset() {
      this.file = null; this.filePreview = null; this.fileDims = ""; this.fileOrientation = null;
      this.resultUrl = null; this.error = ""; this.copied = false; this.busy = false;
    },
    close() { if (!this.busy) this.onClose(); },
    onDrop(e) {
      this.dragOver = false;
      const f = e.dataTransfer?.files?.[0];
      if (f) this.setFile(f);
    },
    onPick(e) {
      const f = e.target.files?.[0];
      if (f) this.setFile(f);
      e.target.value = "";
    },
    setFile(f) {
      if (!/^image\//.test(f.type)) { this.error = "Bitte eine Bilddatei auswählen."; return; }
      this.error = ""; this.resultUrl = null; this.copied = false;
      this.file = f;
      if (this.filePreview) URL.revokeObjectURL(this.filePreview);
      this.filePreview = URL.createObjectURL(f);
      const img = new Image();
      img.onload = () => {
        this.fileDims = `${img.naturalWidth} × ${img.naturalHeight}`;
        this.fileOrientation = img.naturalWidth >= img.naturalHeight ? "landscape" : "portrait";
      };
      img.src = this.filePreview;
    },
    async generate() {
      if (!this.file || !this.mockup) return;
      this.busy = true; this.error = ""; this.resultUrl = null; this.copied = false;
      try {
        // 1) Bild in den Storage (gleicher Bucket, aus dem auch die Render-Funktion liest)
        this.busyText = "Bild wird hochgeladen …";
        const safeName = this.file.name.replace(/[^\w.-]+/g, "_").slice(0, 80);
        const path = `mockups/generator/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safeName}`;
        const storageRef = ref(getStorage(), path);
        await uploadBytes(storageRef, this.file, { contentType: this.file.type });
        const artworkUrl = await getDownloadURL(storageRef);
        // 2) Rendern mit derselben Funktion wie die Vorschau-Mail
        this.busyText = "Mockup wird gerendert …";
        const call = httpsCallable(functions, "generateMockupPreview", { timeout: 300000 });
        const res = await call({ mockupId: this.mockup.uuid, artworkUrl, width: this.width });
        this.resultUrl = res.data?.url || null;
        if (!this.resultUrl) throw new Error("Keine URL zurückbekommen");
      } catch (e) {
        this.error = e?.message || "Generieren fehlgeschlagen.";
      } finally {
        this.busy = false; this.busyText = "";
      }
    },
    async copyUrl() {
      await copyToClipboard(this.resultUrl);
      this.copied = true;
      setTimeout(() => (this.copied = false), 1800);
    },
    async download() {
      try {
        const blob = await (await fetch(this.resultUrl)).blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${(this.mockup?.name || "mockup").replace(/[^\w.-]+/g, "_")}.jpg`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      } catch {
        window.open(this.resultUrl, "_blank", "noopener");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.mg {
  border-radius: 14px !important;
  overflow: hidden;

  &__head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 18px; border-bottom: 1px solid rgba(0, 0, 0, 0.07);
  }
  &__title { display: flex; align-items: center; font-weight: 600; font-size: 15px; }
  &__sub { font-weight: 400; color: rgba(0, 0, 0, 0.5); font-size: 12.5px; margin-left: 10px; }
  // minmax(0, …): sonst dehnt das geladene 4096-px-Vorschaubild die Spalte auf seine Originalbreite aus
  &__body { display: grid; grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); gap: 18px; padding: 18px; }
  &__preview { position: relative; background: #f3f3f3; border-radius: 10px; min-height: 320px; min-width: 0; display: flex; align-items: center; justify-content: center; overflow: hidden; }
  &__side { min-width: 0; }
  &__img { width: 100%; max-width: 100%; flex: 1 1 auto; }
  &__img--dim { opacity: 0.55; }
  &__ph { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; font-size: 12.5px; color: rgba(0, 0, 0, 0.5); }
  &__busy {
    position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;
    background: rgba(255, 255, 255, 0.75); font-size: 13px; color: rgba(0, 0, 0, 0.65);
  }
  &__drop {
    border: 2px dashed rgba(0, 0, 0, 0.18); border-radius: 10px; padding: 18px; min-height: 150px;
    display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; text-align: center;
    cursor: pointer; transition: border-color 0.15s, background 0.15s;
    &:hover, &.is-over { border-color: #282828; background: rgba(0, 0, 0, 0.03); }
    &.has-file { flex-direction: row; text-align: left; justify-content: flex-start; }
  }
  &__drop-text { font-size: 13.5px; color: rgba(0, 0, 0, 0.6); small { color: rgba(0, 0, 0, 0.4); } }
  &__drop-thumb { width: 72px; height: 72px; object-fit: cover; border-radius: 8px; flex: 0 0 auto; }
  &__drop-meta { display: flex; flex-direction: column; gap: 2px; min-width: 0; b { font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } small { font-size: 11.5px; color: rgba(0, 0, 0, 0.5); } }
  &__warn { color: #a96617 !important; }
  &__actions { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 12px; }
  &__error { margin-top: 12px; font-size: 12.5px; color: #b3261e; background: #fdecea; border-radius: 8px; padding: 8px 10px; }
}
// Schmale Fenster (Zoom, DevTools, Tablet): eine Spalte, Bedienung zuerst, Vorschau darunter und gedeckelt
@media (max-width: 820px) {
  .mg__body { grid-template-columns: 1fr; gap: 14px; }
  .mg__side { order: -1; }
  .mg__preview { min-height: 180px; max-height: 300px; overflow: hidden; }
  .mg__img { max-height: 300px !important; }
}
</style>
